/**
 * Round 9 A2 -- what the key the administrator just minted is FOR.
 *
 * The page handed out a credential and said nothing about how to use
 * it: no base URL, no endpoint, no header, no paging rule, no example.
 * The administrator pastes a secret into an email and the GRC team
 * works the rest out by trial, against an API they cannot see the
 * documentation for. And a key with no scopes was accepted and shown as
 * "enabled", so the first thing they meet is a 403 naming a scope
 * nobody chose.
 *
 * Scope-driven rather than a static block: a key minted for
 * `findings:read` has no business being told about the board snapshot,
 * and listing routes the holder cannot call is how an integration
 * starts with a 403 and a support ticket.
 */
import { useTranslation } from "react-i18next";

import { useToast } from "shared/toast/ToastProvider";

/** Technical tokens, not prose: they are the same in every language. */
const API_PREFIX = "/v1/ext";
const KEY_HEADER = "X-API-Key: <key>";

/** What each scope actually unlocks. Mirrors backend SERVICE_SCOPES. */
export const SCOPE_ROUTES: Record<string, { route: string; note: string }> = {
  "findings:read": {
    route: "GET /v1/ext/findings",
    note: "Open findings across the estate, oldest first, cursor-paged.",
  },
  "assessments:read": {
    route: "GET /v1/ext/assessments",
    note: "Control assessments, oldest first, cursor-paged.",
  },
  "board:read": {
    route: "GET /v1/ext/board-snapshot",
    note: "The one-page executive board snapshot. A single document.",
  },
  "cascade:read": {
    route: "GET /v1/ext/cascade",
    note: "Current cascade summary -- impacted IBSes and rollups.",
  },
  "dependencies:read": {
    route: "GET /v1/ext/dependencies",
    note: "What each business service rests on, six layers, paged by code. A snapshot.",
  },
  "ibs_register:read": {
    route: "GET /v1/ext/ibs-register",
    note: "The whole IBS register as one JSON document, for a GRC data feed. Re-sent whole; keyed on code.",
  },
  "telemetry:write": {
    route: "POST /v1/ext/telemetry",
    note: "Push service-health readings in. The one write scope.",
  },
};

export function OutboundContract({ scopes }: { scopes: string[] }) {
  const { t } = useTranslation();
  const toast = useToast();
  const base =
    typeof window === "undefined" ? "https://your-marsad-host" : window.location.origin;
  const baseUrl = base + API_PREFIX;
  const paged = scopes.find((s) => s === "findings:read" || s === "assessments:read");
  // The example is the route the holder will actually call: a paged
  // register if the key has one, else the single document its scope
  // names -- a feed key shown a board-snapshot curl is being told
  // about a route it cannot call.
  const examplePath = paged
    ? "/v1/ext/" + paged.split(":")[0] + "?limit=100"
    : scopes.includes("ibs_register:read")
      ? "/v1/ext/ibs-register"
      : scopes.includes("dependencies:read")
        ? "/v1/ext/dependencies?limit=25"
        : "/v1/ext/board-snapshot";
  const example =
    `curl -H 'X-API-Key: <the key above>' \\\n` + `  '${base}${examplePath}'`;

  return (
    <div
      data-testid="sa-contract"
      className="mt-3 rounded border border-slate-200 bg-white p-3"
    >
      <p className="text-xs font-semibold text-brand-navy">
        {t("screens.serviceAccountsView.contract.heading")}
      </p>

      <dl className="mt-2 space-y-1 text-xs text-slate-600">
        <div className="flex gap-2">
          <dt className="w-20 shrink-0 text-slate-500">
            {t("screens.serviceAccountsView.contract.baseUrl")}
          </dt>
          <dd className="font-mono" data-testid="sa-contract-base">
            {baseUrl}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-20 shrink-0 text-slate-500">
            {t("screens.serviceAccountsView.contract.header")}
          </dt>
          <dd className="font-mono">{KEY_HEADER}</dd>
        </div>
      </dl>

      <p className="mt-3 text-xs font-semibold text-slate-500">
        {t("screens.serviceAccountsView.contract.thisKeyOpens")}
      </p>
      <ul className="mt-1 space-y-1" data-testid="sa-contract-routes">
        {scopes.map((s) => {
          const row = SCOPE_ROUTES[s];
          return (
            <li key={s} className="text-xs">
              <code className="font-mono text-brand-navy">{row ? row.route : s}</code>
              {row ? <span className="text-slate-500"> -- {row.note}</span> : null}
            </li>
          );
        })}
      </ul>

      {paged ? (
        <p className="mt-3 text-xs text-slate-600" data-testid="sa-contract-cursor">
          {t("screens.serviceAccountsView.contract.cursor")}
        </p>
      ) : null}

      <div className="mt-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold text-slate-500">
            {t("screens.serviceAccountsView.contract.example")}
          </p>
          <button
            type="button"
            data-testid="sa-contract-copy"
            onClick={() => {
              void navigator.clipboard?.writeText(example);
              toast.push({
                tone: "success",
                message: t("screens.serviceAccountsView.contract.copied"),
              });
            }}
            className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50"
          >
            {t("screens.serviceAccountsView.copy")}
          </button>
        </div>
        <pre className="mt-1 overflow-x-auto rounded bg-slate-50 px-2 py-1.5 font-mono text-[11px] text-slate-800 ring-1 ring-slate-200">
          {example}
        </pre>
      </div>

      {/* Where the holder's pulls show up. Round 9 A1 made this true;
          saying so here is what turns it into a control the
          administrator knows he has. */}
      <p className="mt-3 text-xs text-slate-500" data-testid="sa-contract-audited">
        {t("screens.serviceAccountsView.contract.audited")}
      </p>
    </div>
  );
}
